import { A } from '@ember/array';
import { readOnly, reads } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { task } from 'ember-concurrency';

import InvalidCredentialsError from 'later/errors/invalid-credentials-error';
import IgPost from 'later/models/ig-post';
import { OauthSocialProfileType } from 'later/utils/constants';
import { fetch } from 'later/utils/fetch';
import objectPromiseProxy from 'later/utils/object-promise-proxy';
import redirect from 'shared/utils/redirect';

import type { Hashtag } from 'collect/types/hashtag-search';
import type { TaskInstance } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type UserAgentService from 'ember-useragent/services/user-agent';
import type GroupModel from 'later/models/group';
import type IgComment from 'later/models/ig-comment';
import type SocialProfileModel from 'later/models/social-profile';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type ErrorsService from 'later/services/errors';
import type InstagramBasicDisplayService from 'later/services/instagram-basic-display';
import type InstagramGraphService from 'later/services/instagram-graph';
import type {
  FetchHashtagParams,
  FetchMediaParams,
  GraphCallParams,
  InstagramPosts
} from 'later/services/instagram-graph';
import type JWTService from 'later/services/jwt';
import type LaterConfigService from 'later/services/later-config';
import type SocialAuthEventsService from 'later/services/social/auth-events';
import type { SocialAuthEvent, SocialAuthEventFlow, SocialAuthEventFlowId } from 'later/services/social/auth-events';
import type ConnectProfilesService from 'later/services/social/connect-profiles';
import type IgUser from 'shared/models/ig-user';
import type { Maybe } from 'shared/types';
import type {
  InstagramCommentReply,
  InstagramGraphUser,
  InstagramPagination,
  InstagramRequestError,
  RecentInstagramMedia
} from 'shared/types/instagram';

interface InstagramLoginData {
  credentials: { token: string };
  uid: string;
  permissionScope: string;
  scope: string[];
}

interface InstagramLoginFailure {
  error: true;
  reason?: string;
  flowId?: SocialAuthEventFlowId | null;
}

interface InstagramLoginEvent extends Event {
  origin?: string;
  source?: MessageEventSource | null;
  data?: unknown;
}

interface InstagramAuthEventContext {
  flow: SocialAuthEventFlow;
  flowId?: SocialAuthEventFlowId;
  groupId?: string;
  socialProfileId?: string;
  hasEmittedPopupBlockedFailure?: boolean;
}

interface UsernameCacheQuery {
  [key: string]: string[];
}

/**
 * This service acts as an entry point for interacting with the
 * Instagram API. It's intent it to automatically decide whether
 * to use the Instagram Graph API or the Basic Display API by
 * looking at the social profile provided to a given method call.
 **/
export default class InstagramService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service declare errors: ErrorsService;
  @service declare instagramBasicDisplay: InstagramBasicDisplayService;
  @service declare instagramGraph: InstagramGraphService;
  @service declare intl: IntlService;
  @service declare jwt: JWTService;
  @service declare laterConfig: LaterConfigService;
  @service('social/auth-events') declare authEvents: SocialAuthEventsService;
  @service declare userAgent: UserAgentService;
  @service('social/connect-profiles') declare connectProfiles: ConnectProfilesService;

  @readOnly('instagramGraph.errorCode') declare errorCode?: number;
  @readOnly('auth.currentUserModel.facebookToken') declare facebookUserToken: string;
  @reads('laterConfig.instagramClientId') declare instagramClientId: string;
  @reads('auth.currentSocialProfile') declare socialProfile: SocialProfileModel;

  /**
   * Nullifies the errorCode property on the InstagramGraph Service.
   * This method is a direct call-through to the InstagramGraph Service
   */
  clearError(): void {
    this.instagramGraph.set('errorCode', undefined);
  }

  /**
   * Reconnect an Instagram profile through OAuth.
   */
  async loginInstagram(
    socialProfile: SocialProfileModel,
    flowId: SocialAuthEventFlowId
  ): Promise<InstagramLoginData | void> {
    const eventContext: InstagramAuthEventContext = {
      flow: 'reconnect',
      flowId,
      socialProfileId: socialProfile.id
    };

    const path = this.connectProfiles.oAuthPath({
      socialProfileType: OauthSocialProfileType.Instagram,
      flowId
    });

    const popup = await this.#makePopupWindow('', eventContext);
    if (popup) {
      popup.location = `${path}&social_profile_id=${socialProfile.id}`;
      return this.#listenPopupEvent(popup, eventContext);
    }

    const fullPath = isNone(this.auth.currentGroup) ? path : `${path}&group_id=${this.auth.currentGroup.id}`;

    // On mobile devices, use a popup to prevent Instagram app from intercepting the OAuth flow
    if (this.userAgent.device.isMobile) {
      const popup = await this.#makePopupWindow('', eventContext);
      if (popup) {
        popup.location = fullPath;
        return;
      }
    }

    // Desktop: use regular redirect
    redirect(fullPath);
  }

  /**
   * Perform OAuth request with Instagram
   */
  async createInstagramWithSet(
    setId: Maybe<string>,
    group: GroupModel,
    redirectPath: Maybe<string>,
    flowId?: SocialAuthEventFlowId
  ): Promise<void> {
    const path = this.connectProfiles.oAuthPath({
      socialProfileType: OauthSocialProfileType.Instagram,
      redirectGroupSlug: group.slug,
      redirectPath,
      flowId
    });

    const fullPath = isNone(setId) ? `${path}&group_id=${group.id}` : `${path}&social_identity_id=${setId}`;
    const eventContext: InstagramAuthEventContext = { flow: 'connect', flowId, groupId: group.id };

    // On mobile devices, use a popup to prevent Instagram app from intercepting the OAuth flow
    if (this.userAgent.device.isMobile) {
      const popup = await this.#makePopupWindow('', eventContext);
      if (popup) {
        popup.location = fullPath;
        return;
      }
    }

    // Desktop: use regular redirect
    redirect(fullPath);
  }

  async refreshInstagramToken(socialProfileProxy: SocialProfileModel): Promise<SocialProfileModel> {
    const socialProfile = (await objectPromiseProxy(socialProfileProxy)) as SocialProfileModel;
    const flowId = this.authEvents.mintFlowId();
    this.#emitAuthEvent({ flow: 'reconnect', flowId, socialProfileId: socialProfile.id }, { stage: 'started' });
    // Redirect fallbacks unload the page; only popup completion continues here.
    const auth = (await this.loginInstagram(socialProfile, flowId)) as InstagramLoginData;
    const {
      uid,
      credentials: { token },
      permissionScope
    } = auth;
    const username = this.fetchBasicDisplayUsername(token);

    if (uid === socialProfile.uid || (await username) === socialProfile.nickname) {
      const scopes = socialProfile.permissionScope || A([]);
      scopes.pushObjects([permissionScope]);
      socialProfile.setProperties({
        basicDisplayId: uid,
        basicDisplayToken: token
      });
      socialProfile.set('permissionScope', scopes.uniq());
      await socialProfile.save();
      this.jwt.clearToken();
      this.alerts.success(this.intl.t('alerts.account.controllers.groups.reconnect_success'));
      return socialProfile;
    }
    throw new Error(
      this.intl.t('alerts.account.controllers.groups.wrong_profile', {
        social_profile: socialProfile.nickname,
        username
      })
    );
  }

  /**
   * Fetches Recent Media (Posts) for specified socialProfile,
   * can handle paging through this endpoint.
   */
  async fetchRecentMedia(
    socialProfileProxy: SocialProfileModel,
    params: FetchMediaParams,
    pagination?: InstagramPagination | Record<string, never>
  ): Promise<RecentInstagramMedia | undefined> {
    const socialProfile = (await objectPromiseProxy(socialProfileProxy)) as SocialProfileModel;
    const isBusiness = socialProfile.hasProfessionalAccount;

    if (socialProfile.needsRefresh) {
      throw new InvalidCredentialsError(
        this.intl.t('alerts.errors.auto_publish.expired_token.message', { account_name: socialProfile.nickname })
      );
    }

    const paramsWithPagination = Object.assign(
      {},
      params,
      pagination && pagination.cursors && isBusiness ? { after: pagination.cursors.after } : {}
    );

    if (isBusiness) {
      try {
        const response = await this.instagramGraph.fetchRecentMedia(socialProfile, paramsWithPagination);
        return response ? Object.assign({}, response, { isBusiness }) : undefined;
      } catch (error) {
        this.alerts.warning(this.intl.t('alerts.generic_error_message'), error);
        throw new InvalidCredentialsError(error);
      }
    }

    return undefined;
  }

  /**
   * Calls the {instagramGraph,instagramBasicDisplay}.fetchSelf method
   */
  async fetchSelf(socialProfile: SocialProfileModel): Promise<IgUser | InstagramGraphUser | undefined> {
    if (socialProfile.hasProfessionalAccount) {
      try {
        return await this.instagramGraph.fetchSelf(socialProfile);
      } catch (error) {
        if (this.auth.currentAccount?.rolloutIgWithFbLoginOauth) {
          return undefined;
        }
        return await this.instagramBasicDisplay.fetchSelf.perform(socialProfile);
      }
    }

    if (this.auth.currentAccount?.rolloutIgWithFbLoginOauth) {
      return undefined;
    }

    return this.instagramBasicDisplay.fetchSelf.perform(socialProfile);
  }

  /**
   * Fetches specified Instagram Media
   *
   * @param socialProfile The socialProfile making the request
   * @param mediaId Instagram Media's ID. Either from IG or Graph APIs
   * @param params Endpoint specific parameters
   *
   * @returns Formatted Instagram Post
   */
  async fetchMedia(
    socialProfile: SocialProfileModel,
    mediaId: string,
    params: FetchMediaParams
  ): Promise<IgPost | undefined> {
    const isProfessional = socialProfile.hasProfessionalAccount;
    if (isProfessional) {
      try {
        return await this.instagramGraph.fetchMedia(socialProfile, mediaId, params);
      } catch (error) {
        return await this.instagramBasicDisplay.fetchMedia.perform(socialProfile, mediaId);
      }
    }

    return this.instagramBasicDisplay.fetchMedia.perform(socialProfile, mediaId);
  }

  fetchMediaComments(socialProfile: SocialProfileModel, post: IgPost, pagingInfo?: string): Promise<IgComment[]> {
    return this.instagramGraph.fetchMediaComments(socialProfile, post, pagingInfo);
  }

  /**
   * Fetches the Instagram Posts where the requesting user has
   * been tagged by another Instagram User.
   *
   * @param params Endpoint specific parameters. See https://developers.facebook.com/docs/instagram-api/reference/user/tags
   */
  fetchTaggedMedia(
    socialProfile: SocialProfileModel,
    params: FetchMediaParams & FetchHashtagParams
  ): Promise<InstagramPosts> {
    return this.instagramGraph.fetchTaggedMedia(socialProfile, params);
  }

  /**
   * Fetches a specific hashtag.
   * This method is a direct call-through to the InstagramGraph Service
   */
  fetchHashtag(socialProfile: SocialProfileModel, name: string): Promise<Hashtag> {
    return this.instagramGraph.fetchHashtag(socialProfile, name);
  }

  /**
   * Fetches recent media that has been tagged with a specific hashtag
   * This method is a direct call-through to the InstagramGraph Service
   *
   * @param params Endpoint specific parameters. See https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/reference/ig-hashtag/recent-media/
   */
  fetchHashtagMedia(
    endpoint = 'recent_media',
    socialProfile: SocialProfileModel,
    hashtagId: string,
    params: FetchHashtagParams = { limit: 10 }
  ): Promise<InstagramPosts> {
    return this.instagramGraph.fetchHashtagMedia(endpoint, socialProfile, hashtagId, params);
  }

  /**
   * Fetches a specific hashtag by id
   * This method is a direct call-through to the InstagramGraph Service
   *
   * @param params Endpoint specific parameters. See https://developers.facebook.com/docs/instagram-api/reference/hashtag
   */
  fetchHashtagById(id: string, socialProfile: SocialProfileModel, params: FetchHashtagParams): Promise<Hashtag> {
    return this.instagramGraph.fetchHashtagById(id, socialProfile, params);
  }

  /**
   * Fetches the list of recently searched hashtags for the provided profile
   * This method is a direct call-through to the InstagramGraph Service
   *
   * @param params Endpoint specific parameters. See https://developers.facebook.com/docs/instagram-api/reference/user/recently_searched_hashtags
   */
  fetchRecentHashtag(socialProfile: SocialProfileModel, params: FetchHashtagParams): Promise<Hashtag[]> {
    return this.instagramGraph.fetchRecentHashtag(socialProfile, params);
  }

  /**
   * Get a list of suggested_usernames from cache or by calling the backend api.
   */
  fetchUsernames = task(async (socialProfile: SocialProfileModel, query: string) => {
    const id = socialProfile.get('id');
    const cache: UsernameCacheQuery = (this.cache.retrieve(`usernames_${id}`) as UsernameCacheQuery) ?? {};
    let usernames: string[];

    if (cache[query]) {
      usernames = cache[query];
    } else if (query.length > 1 && cache[query.slice(0, -1)]) {
      usernames = cache[query.slice(0, -1)].filter((username) => username.includes(query.toLowerCase()));
    } else {
      usernames = await this.fetchUsernamesFromApi.perform(id, query);
    }

    cache[query] = usernames;
    this.cache.add(`usernames_${id}`, cache, { expiry: this.cache.expiry(1, 'day') });

    return usernames;
  });

  fetchUsernamesFromApi = task(async (id: string, query: string) => {
    try {
      const result = await fetch(`/api/v2/social_profiles/${id}/collected_usernames?username_input=${query}`);
      return result.usernames;
    } catch (error) {
      this.errors.log(error);
      return [];
    }
  });

  fetchBasicDisplayUsername(token: string): TaskInstance<Maybe<string>> {
    return this.instagramBasicDisplay.fetchUsername.perform(token);
  }

  postReply(
    commentId: string,
    message: string,
    isReply = false,
    socialProfile: SocialProfileModel
  ): Promise<InstagramCommentReply> {
    return this.instagramGraph.postReply(commentId, message, isReply, socialProfile);
  }

  deleteComment(commentId: string, socialProfile: SocialProfileModel, params = {}): Promise<void> {
    return this.instagramGraph.deleteComment(commentId, params, socialProfile);
  }

  graphRequest<T = unknown>(endpoint: string, socialProfile: SocialProfileModel, params?: GraphCallParams): Promise<T> {
    return this.instagramGraph.graphRequest(endpoint, socialProfile, params);
  }

  /**
   * Calls the instagramBasicDisplay.raiseError method
   */
  raiseError(error: InstagramRequestError): void {
    this.instagramBasicDisplay.raiseError(error);
  }

  /**
   * Checks whether the provided pagination object indicates
   * that there is another page of data that can be requested
   *
   * @param pagination The pagination object returned
   * from a previous call to an Instagram API
   */
  hasMorePages(pagination?: InstagramPagination): boolean {
    if (!pagination) {
      return false;
    }

    if (pagination && !pagination.next_url && !pagination.next) {
      return false;
    }

    return true;
  }

  isIgPostArray(posts: unknown[]): posts is IgPost[] {
    return posts.every((post) => post instanceof IgPost);
  }

  /**
   * Attaches an event lister to the oAuth popup window
   */
  #listenPopupEvent(popup: Maybe<Window>, eventContext: InstagramAuthEventContext): Promise<InstagramLoginData> {
    return new Promise((resolve, reject) => {
      const isIEWindow = isNone(window.addEventListener);
      const messageEvent = isIEWindow ? 'onmessage' : 'message';
      let interval: number | undefined;

      try {
        const cleanup = (messageHandler: (event: InstagramLoginEvent) => void): void => {
          if (interval !== undefined) {
            window.clearInterval(interval);
          }
          if (isIEWindow) {
            (window as typeof window & { detachEvent: typeof window.attachEvent }).detachEvent(
              messageEvent,
              messageHandler
            );
          } else {
            window.removeEventListener(messageEvent, messageHandler, false);
          }
        };

        const messageHandler = (event: InstagramLoginEvent): void => {
          console.log('From origin:', event.origin);
          if (event.origin !== document.location.origin) {
            this.#emitAuthEvent(eventContext, {
              stage: 'failed',
              eventName: 'INVALID POPUP MESSAGE ORIGIN'
            });
            cleanup(messageHandler);
            reject('Please retry and login to Instagram to proceed.');
            return;
          }

          if (event.source !== popup) {
            return;
          }

          console.log('parent received message:', event.data);
          if (typeof event.data !== 'object' || event.data === null) {
            return;
          }

          if ('error' in event.data && event.data.error === true) {
            const failure = event.data as InstagramLoginFailure;

            if (failure.flowId && failure.flowId !== eventContext.flowId) {
              return;
            }

            // do not emit any auth events here, these will be handled by the backend
            cleanup(messageHandler);
            reject(failure.reason || 'Could not login to Instagram');
          } else if ('credentials' in event.data) {
            cleanup(messageHandler);
            resolve(event.data as InstagramLoginData);
          }
        };

        interval = window.setInterval(() => {
          if (!popup || popup.closed) {
            this.#emitAuthEvent(eventContext, {
              stage: 'failed',
              reason: 'access_denied',
              eventName: 'POPUP CLOSED'
            });
            cleanup(messageHandler);
            reject('Please retry and login to Instagram to proceed.');
          }
        }, 1000);
        if (isIEWindow) {
          window.attachEvent(messageEvent, messageHandler);
        } else {
          window.addEventListener(messageEvent, messageHandler, false);
        }
      } catch (error) {
        if (interval !== undefined) {
          window.clearInterval(interval);
        }
        this.#emitAuthEvent(eventContext, {
          stage: 'failed',
          errorClass: error instanceof Error ? error.name : undefined,
          errorMessage: error instanceof Error ? error.message : String(error),
          eventName: 'POPUP LISTENER ERROR'
        });
        this.errors.log(error);
        reject('Could not login to Instagram');
      }
    });
  }

  /**
   * Opens an oAuth Window
   */
  async #makePopupWindow(url: string, eventContext: InstagramAuthEventContext): Promise<Maybe<Window>> {
    document.cookie = 'oauth_popup=1; path=/;max-age=60*5';
    const popup = window.open(url, 'oauthWindow', 'location=0,status=0,width=800,height=600');

    if (!popup && !eventContext.hasEmittedPopupBlockedFailure) {
      eventContext.hasEmittedPopupBlockedFailure = true;
      this.#emitAuthEvent(eventContext, { stage: 'failed', reason: 'popup_blocked' });
    }

    return popup;
  }

  #emitAuthEvent(
    eventContext: InstagramAuthEventContext,
    event: Pick<SocialAuthEvent, 'stage' | 'reason' | 'errorClass' | 'errorMessage' | 'eventName'>
  ): void {
    if (!eventContext.flowId) {
      return;
    }

    void this.authEvents.emit({
      provider: 'instagram',
      flow: eventContext.flow,
      flowId: eventContext.flowId,
      ...(eventContext.groupId ? { groupId: eventContext.groupId } : {}),
      ...(eventContext.socialProfileId ? { socialProfileId: eventContext.socialProfileId } : {}),
      isPopup: true,
      ...event
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    instagram: InstagramService;
  }
}
